import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, X, AlertTriangle, Clock, Info, MessageCircle, CheckCheck, ExternalLink, ShieldAlert, Trash2, PenSquare } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${(n ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SEVERITY = {
  critical: { icon: AlertTriangle, dot: "bg-red-500", ring: "ring-red-500/20 bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900", text: "text-red-700 dark:text-red-400", label: "Crítico" },
  warning: { icon: Clock, dot: "bg-amber-500", ring: "ring-amber-500/20 bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900", text: "text-amber-700 dark:text-amber-400", label: "Aviso" },
  info: { icon: Info, dot: "bg-blue-500", ring: "ring-blue-500/20 bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-900", text: "text-[#1E3A8A] dark:text-blue-400", label: "Cortesía" },
};

export default function NotificationBell() {
  const { user } = useAuth();
  const canSeeAudit = user?.role === "dueno" || !!user?.permissions?.auditoria?.view;
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState({ pending_count: 0, critical_count: 0, warning_count: 0, info_count: 0, upcoming_week: [] });
  const [items, setItems] = useState([]);
  const [auditSummary, setAuditSummary] = useState({ pending_count: 0, critical_count: 0, warning_count: 0 });
  const [auditItems, setAuditItems] = useState([]);
  const [tab, setTab] = useState("alertas"); // alertas | proximas | auditoria
  const popRef = useRef(null);

  const load = async () => {
    try {
      const calls = [
        api.get("/notifications/summary"),
        api.get("/notifications?status=pending"),
      ];
      if (canSeeAudit) {
        calls.push(api.get("/audit-alerts/summary"));
        calls.push(api.get("/audit-alerts?status=pending"));
      }
      const results = await Promise.all(calls);
      setSummary(results[0].data);
      setItems(results[1].data);
      if (canSeeAudit) {
        setAuditSummary(results[2].data);
        setAuditItems(results[3].data);
      }
    } catch (_) { /* silent */ }
  };

  useEffect(() => {
    load();
    // Poll every 60 seconds so alerts feel live
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [canSeeAudit]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const dismiss = async (id) => {
    try {
      await api.post(`/notifications/${id}/dismiss`);
      setItems((prev) => prev.filter((n) => n.id !== id));
      load();
    } catch { /* noop */ }
  };

  const markSent = async (n) => {
    try {
      await api.post(`/notifications/${n.id}/mark-sent`);
      setItems((prev) => prev.filter((x) => x.id !== n.id));
      load();
    } catch { /* noop */ }
  };

  const openWhatsApp = (n) => {
    if (!n.wa_url) return toast.error("Cliente sin teléfono");
    window.open(n.wa_url, "_blank");
    markSent(n);
  };

  const runNow = async () => {
    try {
      const { data } = await api.post("/notifications/generate");
      const extra = data.attendance_alerts_created > 0 ? ` · ${data.attendance_alerts_created} retardos/olvidos` : "";
      toast.success(`Escaneo completo · ${data.created} nuevas · ${data.auto_dismissed} auto-cerradas${extra}`);
      load();
    } catch {
      toast.error("Error al ejecutar escaneo");
    }
  };

  const markAllSent = async () => {
    try {
      const { data } = await api.post("/notifications/mark-all-sent");
      toast.success(`${data.marked} marcadas como enviadas`);
      load();
    } catch { toast.error("Error"); }
  };

  const markAuditRead = async (id) => {
    try {
      await api.post(`/audit-alerts/${id}/mark-read`);
      setAuditItems((prev) => prev.filter((a) => a.id !== id));
      load();
    } catch { /* noop */ }
  };

  const markAllAuditRead = async () => {
    try {
      const { data } = await api.post("/audit-alerts/mark-all-read");
      toast.success(`${data.marked} alertas de auditoría marcadas como vistas`);
      load();
    } catch { toast.error("Error"); }
  };

  const count = (summary.pending_count || 0) + (canSeeAudit ? (auditSummary.pending_count || 0) : 0);
  const anyCritical = summary.critical_count > 0 || (canSeeAudit && auditSummary.critical_count > 0);
  const anyWarning = summary.warning_count > 0 || (canSeeAudit && auditSummary.warning_count > 0);
  const dotClass = anyCritical ? "bg-red-500" : anyWarning ? "bg-amber-500" : "bg-[#1E3A8A]";

  return (
    <div className="relative" ref={popRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="notification-bell"
        aria-label={`Notificaciones (${count} pendientes)`}
        className="relative w-8 h-8 rounded-md flex items-center justify-center text-stone-500 hover:text-stone-900 hover:bg-stone-100 dark:hover:bg-stone-800 dark:hover:text-stone-100 transition-colors"
      >
        <Bell className="w-4 h-4" strokeWidth={1.75} />
        {count > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full ${dotClass} text-white text-[9px] font-outfit font-bold flex items-center justify-center px-1 ring-2 ring-white dark:ring-stone-950`}
            data-testid="notification-badge">
            {count > 99 ? "99+" : count}
          </span>
        )}
        {anyCritical && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 animate-ping opacity-40" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[420px] max-h-[600px] bg-white dark:bg-stone-950 border border-stone-200 dark:border-stone-800 rounded-lg shadow-2xl overflow-hidden z-50 flex flex-col"
          data-testid="notification-popover">
          {/* Header */}
          <div className="px-4 py-3 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
            <div>
              <div className="font-outfit font-semibold text-sm text-stone-900 dark:text-stone-100">Centro de alertas</div>
              <div className="text-[11px] text-stone-500 dark:text-stone-400 mt-0.5">Cobranza cada hora{canSeeAudit ? " · auditoría en tiempo real" : ""}</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"><X className="w-4 h-4" /></button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-stone-200 dark:border-stone-800">
            <TabBtn active={tab === "alertas"} onClick={() => setTab("alertas")} count={items.length} label="Cobranza" />
            <TabBtn active={tab === "proximas"} onClick={() => setTab("proximas")} count={summary.upcoming_week?.length || 0} label="Próximas 7d" />
            {canSeeAudit && (
              <TabBtn active={tab === "auditoria"} onClick={() => setTab("auditoria")} count={auditItems.length} label="Auditoría" />
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto max-h-[380px]">
            {tab === "alertas" ? (
              items.length === 0 ? (
                <EmptyState icon={CheckCheck} title="Sin alertas pendientes" hint="El escaneo automático se ejecuta cada hora." />
              ) : (
                <div className="divide-y divide-stone-100 dark:divide-stone-800">
                  {items.map((n) => <AlertRow key={n.id} n={n} onOpenWa={() => openWhatsApp(n)} onDismiss={() => dismiss(n.id)} />)}
                </div>
              )
            ) : tab === "proximas" ? (
              (summary.upcoming_week?.length || 0) === 0 ? (
                <EmptyState icon={Clock} title="Sin cobros por vencer" hint="No hay facturas con vencimiento en los próximos 7 días." />
              ) : (
                <div className="divide-y divide-stone-100 dark:divide-stone-800">
                  {summary.upcoming_week.map((u) => <UpcomingRow key={u.sale_id} u={u} />)}
                </div>
              )
            ) : (
              auditItems.length === 0 ? (
                <EmptyState icon={ShieldAlert} title="Sin alertas de auditoría" hint="Se avisa aquí cuando alguien edita precios/sueldos o elimina un registro." />
              ) : (
                <div className="divide-y divide-stone-100 dark:divide-stone-800">
                  {auditItems.map((a) => <AuditAlertRow key={a.id} a={a} onMarkRead={() => markAuditRead(a.id)} />)}
                </div>
              )
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-2.5 border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/50 flex items-center gap-2">
            {tab === "auditoria" ? (
              <>
                <button onClick={runNow} data-testid="run-scan-btn-audit" className="text-[11px] px-2.5 py-1 rounded bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 font-outfit">
                  Ejecutar escaneo ahora
                </button>
                {auditItems.length > 0 && (
                  <button onClick={markAllAuditRead} data-testid="mark-all-audit-read-btn" className="text-[11px] px-2.5 py-1 rounded bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 font-outfit">
                    Marcar todas como vistas
                  </button>
                )}
                <a href="/auditoria" onClick={() => setOpen(false)} className="text-[11px] px-2.5 py-1 rounded text-[#1E3A8A] dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 font-outfit font-medium ml-auto flex items-center gap-1">
                  Ir a Auditoría <ExternalLink className="w-3 h-3" />
                </a>
              </>
            ) : (
              <>
                <button onClick={runNow} data-testid="run-scan-btn" className="text-[11px] px-2.5 py-1 rounded bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 font-outfit">
                  Ejecutar escaneo ahora
                </button>
                {items.length > 0 && (
                  <button onClick={markAllSent} className="text-[11px] px-2.5 py-1 rounded text-stone-500 hover:text-stone-800 dark:hover:text-stone-200 font-outfit ml-auto">
                    Marcar todas enviadas
                  </button>
                )}
                <a href="/cobranza" onClick={() => setOpen(false)} className="text-[11px] px-2.5 py-1 rounded text-[#1E3A8A] dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 font-outfit font-medium ml-auto flex items-center gap-1">
                  Ir a Cobranza <ExternalLink className="w-3 h-3" />
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const TabBtn = ({ active, onClick, count, label }) => (
  <button onClick={onClick}
    className={`flex-1 px-3 py-2 text-xs font-outfit font-medium border-b-2 transition-colors ${
      active ? "border-[#1E3A8A] text-[#1E3A8A] dark:border-blue-400 dark:text-blue-400" : "border-transparent text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"
    }`}>
    {label} {count > 0 && <span className={`ml-1 inline-flex items-center justify-center min-w-[16px] h-4 rounded-full text-[9px] font-bold px-1 ${active ? "bg-[#1E3A8A] text-white dark:bg-blue-400 dark:text-stone-950" : "bg-stone-200 dark:bg-stone-800 text-stone-700 dark:text-stone-300"}`}>{count}</span>}
  </button>
);

const AlertRow = ({ n, onOpenWa, onDismiss }) => {
  const sev = SEVERITY[n.severity] || SEVERITY.info;
  const Icon = sev.icon;
  return (
    <div className={`px-4 py-3 hover:bg-stone-50 dark:hover:bg-stone-900/50 relative`} data-testid={`notif-row-${n.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ring-4 ${sev.ring}`}>
          <Icon className={`w-4 h-4 ${sev.text}`} strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-outfit font-semibold text-sm text-stone-900 dark:text-stone-100 truncate">{n.customer_name}</span>
            <span className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-400">·</span>
            <span className="text-xs text-[#1E3A8A] dark:text-blue-400 font-medium">{n.sale_folio}</span>
          </div>
          <div className="text-xs text-stone-600 dark:text-stone-400 mt-0.5">
            {n.days_relative > 0 ? (
              <span className={sev.text}>Vencida hace <b>{n.days_relative} día(s)</b></span>
            ) : n.days_relative === 0 ? (
              <span className={sev.text}>Vence <b>hoy</b></span>
            ) : (
              <span>Vence en <b>{Math.abs(n.days_relative)} día(s)</b> · {n.due_date}</span>
            )}
            <span className="text-stone-400"> · saldo <b className="text-stone-800 dark:text-stone-200 tabular-nums">{money(n.balance)}</b></span>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-stone-400 mt-1 font-outfit">{n.rule_name}</div>
          <div className="flex items-center gap-1 mt-2">
            {n.wa_url ? (
              <button onClick={onOpenWa} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-medium font-outfit">
                <MessageCircle className="w-3 h-3" /> Enviar por WhatsApp
              </button>
            ) : (
              <span className="text-[10px] text-stone-400 italic">Cliente sin teléfono</span>
            )}
            <button onClick={onDismiss} className="text-[11px] text-stone-500 hover:text-stone-800 dark:hover:text-stone-200 px-2 py-1 font-outfit">
              Descartar
            </button>
          </div>
        </div>
      </div>
      <span className={`absolute left-0 top-0 bottom-0 w-0.5 ${sev.dot}`} />
    </div>
  );
};

const UpcomingRow = ({ u }) => (
  <div className="px-4 py-3 hover:bg-stone-50 dark:hover:bg-stone-900/50">
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-outfit font-semibold text-sm text-stone-900 dark:text-stone-100 truncate">{u.customer_name}</span>
          <span className="text-xs text-[#1E3A8A] dark:text-blue-400">{u.folio}</span>
        </div>
        <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
          {u.days_until_due === 0 ? <span className="text-amber-700 dark:text-amber-400 font-medium">Vence hoy</span> : <>Vence en <b>{u.days_until_due} día(s)</b> · {u.due_date}</>}
        </div>
      </div>
      <div className="text-sm tabular-nums font-semibold text-stone-800 dark:text-stone-200">{money(u.balance)}</div>
    </div>
  </div>
);

const AuditAlertRow = ({ a, onMarkRead }) => {
  const sev = SEVERITY[a.severity] || SEVERITY.warning;
  const ActionIcon = a.action === "delete" ? Trash2 : PenSquare;
  return (
    <div className="px-4 py-3 hover:bg-stone-50 dark:hover:bg-stone-900/50 relative" data-testid={`audit-alert-row-${a.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ring-4 ${sev.ring}`}>
          <ActionIcon className={`w-4 h-4 ${sev.text}`} strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className={`text-[10px] uppercase tracking-wider font-outfit font-semibold px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-800 ${sev.text}`}>{sev.label}</span>
            <span className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-400">{a.module}</span>
          </div>
          <div className="text-xs text-stone-700 dark:text-stone-300 mt-1 leading-snug">{a.description}</div>
          <div className="text-[10px] text-stone-400 mt-1 font-outfit">{a.user_name} · {new Date(a.created_at).toLocaleString("es-MX")}</div>
          <div className="flex items-center gap-1 mt-2">
            <button onClick={onMarkRead} className="text-[11px] text-stone-500 hover:text-stone-800 dark:hover:text-stone-200 px-2 py-1 font-outfit" data-testid={`audit-alert-dismiss-${a.id}`}>
              Marcar como vista
            </button>
          </div>
        </div>
      </div>
      <span className={`absolute left-0 top-0 bottom-0 w-0.5 ${sev.dot}`} />
    </div>
  );
};

const EmptyState = ({ icon: Icon, title, hint }) => (
  <div className="py-10 px-6 text-center">
    <div className="w-10 h-10 rounded-full bg-stone-100 dark:bg-stone-800 flex items-center justify-center mx-auto mb-2">
      <Icon className="w-5 h-5 text-stone-400" strokeWidth={1.75} />
    </div>
    <div className="text-sm font-outfit font-medium text-stone-700 dark:text-stone-300">{title}</div>
    <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">{hint}</div>
  </div>
);
